import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Icon from "../../../assets/icons/Icon";

const findNodeByValue = (nodes = [], targetValue) => {
  for (const node of nodes) {
    if (node.value === targetValue) {
      return node;
    }

    if (node.children?.length) {
      const matchedNode = findNodeByValue(node.children, targetValue);

      if (matchedNode) {
        return matchedNode;
      }
    }
  }

  return null;
};

const normalizeSelectedValue = (value, options) => {
  if (!value) {
    return null;
  }

  if (typeof value === "object" && value.value !== undefined) {
    if (value.label) {
      return value;
    }

    const matchedNode = findNodeByValue(options, value.value);

    return matchedNode
      ? {
          label: matchedNode.label,
          value: matchedNode.value,
        }
      : value;
  }

  const matchedNode = findNodeByValue(options, value);

  return matchedNode
    ? {
        label: matchedNode.label,
        value: matchedNode.value,
      }
    : null;
};

const escapeRegExp = (value = "") =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const TreeSelect = ({
  options = [],
  value,
  onChange,
  isClearable = true,
  placeholder = "Select...",
  error = null,
  disabled = false,
  tabIndex,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const selectedOption = useMemo(
    () => normalizeSelectedValue(value, options),
    [options, value],
  );

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchText("");
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [closeDropdown]);

  const handleSelect = (node) => {
    // Prevent selection if the component or the node itself is disabled
    if (disabled || node.disabled) return;
    console.log("🚀 ~ handleSelect ~ node:", node);

    onChange({
      label: node.label,
      value: node.value,
      ...node,
    });

    closeDropdown();
  };

  const handleClear = (event) => {
    event.stopPropagation();

    if (disabled) return;

    onChange(null);
    closeDropdown();
  };

  const filterNodes = (nodes, text) => {
    if (!text) {
      return nodes;
    }

    return nodes.reduce((acc, node) => {
      const matchedChildren = node.children
        ? filterNodes(node.children, text)
        : [];

      if (
        node.label.toLowerCase().includes(text.toLowerCase()) ||
        matchedChildren.length > 0
      ) {
        acc.push({
          ...node,
          children: matchedChildren,
        });
      }

      return acc;
    }, []);
  };

  const filteredOptions = filterNodes(options, searchText);

  const inputValue = isOpen ? searchText : selectedOption?.label || "";

  const handleInputFocus = () => {
    if (disabled) return;

    setIsOpen(true);
  };

  const handleInputChange = (event) => {
    if (disabled) return;

    if (!isOpen) {
      setIsOpen(true);
    }

    setSearchText(event.target.value);
  };

  const handleInputKeyDown = (event) => {
    if (disabled) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setIsOpen(true);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      closeDropdown();
      inputRef.current?.blur();
    }
  };

  const handleArrowToggle = (event) => {
    event.stopPropagation();

    if (disabled) return;

    if (isOpen) {
      closeDropdown();
      inputRef.current?.blur();
      return;
    }

    setIsOpen(true);
    inputRef.current?.focus();
  };

  return (
    <div
      className={`tree-select ${disabled ? "tree-select--disabled" : ""}`}
      ref={dropdownRef}
    >
      <div
        className={`tree-select__control ${
          isOpen ? "tree-select__control--focused" : ""
        } ${error ? "tree-select__control--error" : ""} ${
          disabled ? "tree-select__control--disabled" : ""
        }`}
        onClick={() => {
          if (!disabled) {
            inputRef.current?.focus();
          }
        }}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="tree"
        aria-disabled={disabled}
      >
        <div className="tree-select__value-container">
          <input
            ref={inputRef}
            type="text"
            className="tree-select__input"
            value={inputValue}
            placeholder={isOpen ? "Search..." : placeholder}
            onFocus={handleInputFocus}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            autoComplete="off"
            aria-autocomplete="list"
            disabled={disabled}
            tabIndex={tabIndex}
          />
        </div>

        <div className="tree-select__indicators">
          {isClearable && selectedOption && !disabled && (
            <button
              type="button"
              className="tree-select__clear"
              onClick={handleClear}
              aria-label="Clear selection"
            >
              <Icon name="X" />
            </button>
          )}

          <span className="tree-select__separator" />

          <button
            type="button"
            className={`tree-select__arrow ${
              isOpen ? "tree-select__arrow--open" : ""
            }`}
            onClick={handleArrowToggle}
            aria-label={isOpen ? "Close options" : "Open options"}
            disabled={disabled}
          >
            <Icon name="ChevronDown" />
          </button>
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="tree-select__dropdown">
          <div className="tree-select__menu tree-node__children" role="tree">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((node) => (
                <TreeNode
                  key={node.value}
                  node={node}
                  selectedValue={selectedOption?.value}
                  onSelect={handleSelect}
                  searchText={searchText}
                />
              ))
            ) : (
              <div className="tree-select__no-options">
                <p>No options found</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const TreeNode = ({ node, selectedValue, onSelect, depth = 0, searchText }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const hasChildren = node.children?.length > 0;
  const isSelected = selectedValue === node.value;
  const isDisabled = node.isDisabled;

  useEffect(() => {
    if (searchText) {
      setIsExpanded(true);
    }
  }, [searchText]);

  const highlightText = (text, search) => {
    if (!search) return text;

    const parts = text.split(new RegExp(`(${escapeRegExp(search)})`, "gi"));

    return parts.map((part, i) =>
      part.toLowerCase() === search.toLowerCase() ? (
        <mark key={i} className="tree-node__highlight">
          {part}
        </mark>
      ) : (
        part
      ),
    );
  };

  return (
    <div className="tree-node tree-node__child-wrapper ">
      <div
        className={`tree-node__item ${
          isSelected ? "tree-node__item--selected" : ""
        } ${isDisabled ? "tree-node__item--disabled" : ""}`}
      >
        {hasChildren ? (
          <button
            type="button"
            className="tree-node__toggle"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded((prev) => !prev);
            }}
            aria-label={isExpanded ? "Collapse option" : "Expand option"}
          >
            <Icon
              name="ChevronDown"
              className={isExpanded ? "tree-node__toggle--expanded" : ""}
            />
          </button>
        ) : (
          <span className="tree-node__toggle tree-node__toggle--empty" />
        )}

        <button
          type="button"
          className={`tree-node__label ${
            isDisabled ? "tree-node__label--disabled" : ""
          }`}
          disabled={isDisabled}
          aria-disabled={isDisabled}
          onClick={() => {
            if (!isDisabled) {
              onSelect(node);
            }
          }}
        >
          {highlightText(node.label, searchText)}
        </button>
      </div>

      {hasChildren && isExpanded && (
        <div className="tree-node__children">
          {node.children.map((child, index) => (
            <div
              key={child.value}
              className={`tree-node__child-wrapper ${
                index === node.children.length - 1
                  ? "tree-node__child-wrapper--last"
                  : ""
              }`}
            >
              <TreeNode
                node={child}
                selectedValue={selectedValue}
                onSelect={onSelect}
                depth={depth + 1}
                searchText={searchText}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TreeSelect;
